<template>
  <config-base class="opc-zs-trial" :show-plan-details="showPlanDetails">
    <template #configDesc>
    <div class="opc-zs-trial__configDesc" ref="configDesc">
      <p class="opc-zs-trial__desc-text">
        <img src="@/assets/plans/checked_checkboxes-green.svg" alt="Include" />
        {{ $t('billing.events.saas.freeTrialDescriptions.duration', [trialDays]) }}
      </p>
      <p class="opc-zs-trial__desc-text">
        <img src="@/assets/plans/checked_checkboxes-green.svg" alt="" />
        {{ capacityDesc }}
      </p>
      <p class="opc-zs-trial__desc-text" v-if="!enableRemoveSessionFreeTrailLimit">
        <img src="@/assets/plans/checked_checkboxes-green.svg" alt="" />
        {{ $t('billing.events.saas.freeTrialDescriptions.publish') }}
      </p>
      <p class="opc-zs-trial__desc-text">
        <img src="@/assets/plans/checked_checkboxes-green.svg" alt="" />
        {{ $t('billing.events.saas.freeTrialDescriptions.cancel') }}
      </p>
      <p class="opc-zs-trial__additional-message">
        {{ $t('billing.events.saas.freeTrialDescriptions.requirement') }}
      </p>
    </div>
    </template>
    <template #config>
    <div class="opc-zs-trial__config" ref="config">
      <div class="divide-line" />
      <config-cycle
        v-model="billingCycle"
        :billing-cycle-list="billingCycleList"
        @switchCycleAction="switchCycleAction"
        :unit="unit"
        :show-cycle-alert="showCycleAlert"
        :trackingPlanName="trackingPlanName"
      />
    </div>
    </template>
  </config-base>
</template>

<script type="text/jsx" lang="jsx">
import _ from 'lodash';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import mx_plansCommon from '@/plans/mx/plan';
import planCommonMixin from '@/plans/EVENT_SAAS_CORE/mixin';
import PlanDetailsMixin from '@/plans/EVENT_SAAS/component/dialog/plan-details/mx-plan-details.js';
import FreeTrialTracking from '@/plans/mx/free-trial-tracking.js';
import ConfigBase from '@/components/config-base';
import { BILLING_CYCLE, BillingCycleEnum, DEFAULT_FREE_TRIAL_DAYS, planTypes } from '@/common/constants';
import { plansof } from '@/utils/chain';
import ConfigCycle from '@/components/service/config-billing-cycle/index';
import { planUnitMap } from '@/common/product-unit';
import AddonMixin from '@/plans/mx/addon';
import { isEmptyVal } from '@/utils';
import { pageConfig } from "@/common/service/page-config";
import { getPlanUnitWithCycle } from '@/common/product-unit';
import { ZOOM_MEETING_SUB_TYPES } from '@/plans/ZOOM_MEETING/common/utils';
import { getPlanTypeByCategory } from '@/common/service/parse';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';

const ConfigBaseComp = {
  name: 'eventsaascore-free-base',
  extends: ConfigBase,
  mixins: [planCommonMixin, PlanDetailsMixin, FreeTrialTracking],
  props: {
    showPlanDetails: Boolean
  },
  data() {
    return {
      unit: planUnitMap.license,
    };
  },
  computed: {
    trackingPlanKey() {
      const planName = getTrackingProductNameByPlanType(this.planType);
      return `${planName} free trial`;
    }
  },
  methods: {
    // FREE TRIAL - {plan title} Free Trial
    mx_renderTitle() {
        const isShowCheckIcon = this.hasSelectedPlan;

        return <h2 class="opc-zm-base-card__name text-capitalize">
        {this.$t('billing.trial.title', [this.title])}
        {isShowCheckIcon ? <i check-icon class="zm-icon-success main-icon" aria-hidden="true" /> : null}
        </h2>;
    },
    mx_renderDivideLine() {
      return <div class="divide-line-transparent" />;
    }
  }
};

const ConfigCycleFreeTrial = {
  name: 'eventsaascore-free-trial-config-cycle',
  extends: ConfigCycle,
  methods: {
    // FREE TRIAL - {plan license unit} after trial
    mx_renderCycleInnerVNode(item = {}) {
      let { plan = {}, disabledInnerDesc = '' } = item;
      return disabledInnerDesc ?
        <p class="opc-cycle__disabled--desc">{disabledInnerDesc}</p> :
        <div class="opc-cycle__plan__price">
          <p class="opc-cycle__plan__price--amount">
            {this.$money(plan.amount, { forceDiff: true })}
          </p>
          <p class="opc-cycle__plan__price--unit">
            {this.$t('billing.trial.unit_suffix', [this.$t(plan.billing_cycle ? getPlanUnitWithCycle(plan.billing_cycle, this.unit)  : '')])}<priceVATTip />
          </p>
        </div>;
    }
  }
}

const enableRemoveSessionFreeTrailLimit = window.newBillingConfig?.enableRemoveSessionFreeTrailLimit;

export default {
  mixins: [mx_plansCommon, planCommonMixin, AddonMixin],
  components: {
    ConfigCycle: ConfigCycleFreeTrial,
    ConfigBase: ConfigBaseComp,
  },
  data() {
    return {
      trialDays: DEFAULT_FREE_TRIAL_DAYS,
      capacity: 100,
      selectedNumber: 1,
      billingCycle: '',
      trackingPlanName: getTrackingProductNameByPlanType(planTypes.EVENT_SAAS_CORE),
      showCycleAlert: true,
      showPlanDetails: true,
      enableRemoveSessionFreeTrailLimit,
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'planMapFlat', 'getUserCurrentSubscriptions']),
    planList() {
      return plansof(this.planMapFlat).type(this.planType).get();
    },
    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },
    currentPlanCycleDepFirstPlan() {
      const dependCyclePlanList = _.get(this.planList, [0, 'billingCycleDependBasePlanTypeList'], []);
      let currentSubscriptions = _.clone(this.getUserCurrentSubscriptions);
      // ZOOM-660835: if biz = monthly, biz plus = yearly, ignore biz plus
      const isBizPlusCycleLargerThanBiz = plansof(this.getUserCurrentSubscriptions).category(ZOOM_MEETING_SUB_TYPES.BIZ).monthly().has() &&
                                          plansof(this.getUserCurrentSubscriptions).filter(x => [planTypes.ZOOM_ONE_BUNDLE].includes(getPlanTypeByCategory(x.category))).annual().has();

      if(isBizPlusCycleLargerThanBiz) {
        currentSubscriptions = _.clone(this.getUserCurrentSubscriptions.filter(x => ![planTypes.ZOOM_ONE_BUNDLE].includes(getPlanTypeByCategory(x.category))));
      }
      return _.chain(currentSubscriptions)
        .filter((p) => _.includes(dependCyclePlanList, p.type))
        .filter((p) => !p.cancelled)
        .orderBy((p) => p.cycleValue, 'desc')
        .get(0)
        .value();
    },
    monthlyPlan() {
      const isMonthlyAvailable = plansof(this.planList).capacity(this.capacity).monthly().has();
      if(!isMonthlyAvailable) {
        return { plan: { billing_cycle: BILLING_CYCLE.MONTHLY }, disabledInnerDesc: this.$t('billing.events.onlyForAttendeesDesc', [100]), disabled: true };
      }
      const monthlyPlan = plansof(this.planList).capacity(this.capacity).monthly().get(0);
      const basicItem = { plan: monthlyPlan, disabled: !monthlyPlan.isOnlineFreeTrial, disabledTooltip: '' };
      return basicItem;
    },
    annualPlan() {
      const annualPlan = plansof(this.planList).capacity(this.capacity).annual().get(0);

      if (!annualPlan) return null;

      const depFirstPlan = this.currentPlanCycleDepFirstPlan;

      const basicItem = { plan: annualPlan, disabled: !annualPlan.isOnlineFreeTrial, disabledTooltip: '' };

      // check for depend plan list with billing cycle
      if (depFirstPlan?.cycleValue < BillingCycleEnum[BILLING_CYCLE.ANNUAL]) {
        const basePlanName = this.$t(depFirstPlan?.typeNameKey);

        const alert = this.$t('billing.planform.can_not_select_annual_with_base', [basePlanName]);
        const tooltips = this.$t('billing.planform.annual_addon_disable_tooltip', [basePlanName, this.title, basePlanName]);

        return {
          ...basicItem,
          alert,
          tooltips,
          disabled: true,
        };
      }

      return basicItem;
    },
    billingCycleList() {
      return [this.annualPlan, this.monthlyPlan].filter((p) => p.plan);
    },
    capacityDesc() {
      if (enableRemoveSessionFreeTrailLimit) {
        return this.$t('billing.events.saas.freeTrialDescriptions.capacity_unlimited', [this.capacity]);
      }
      return this.$t('billing.events.saas.freeTrialDescriptions.capacity', [this.capacity]);
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchBillingCycleAction']),
    async switchCycleAction(oldPlan, newPlan) {
      const { quantity = 1 } = pageConfig || {}
      // FREE TRIAL - quantity can't be changed, usually is only 1
      const newPlanInfo = { ...newPlan, cartInfo: { selected_number: quantity } };
      this.trialDays = newPlanInfo.trialDays ?? this.trialDays;
      await this.switchBillingCycleAction([
        oldPlan?.plan_code,
        newPlanInfo,
      ]);

      this.afterSwitchCycleTracking();
    },
    afterSwitchCycleTracking() {
      // event tracking
      const billingCycle = this.selectedPlan?.billing_cycle;
      billingCycle && this.$tracking.buyInteraction(
        `${this.trackingPlanKey} plan`,
        this.$tEn(`billing.billing_cycle.${billingCycle}`)?.toLowerCase(),
      );
    }
  },
  created() {
    const selectedPlan = this.selectedPlan;
    if (!isEmptyVal(selectedPlan)) {
      this.trialDays = selectedPlan?.trialDays ?? this.trialDays;
      this.capacity = selectedPlan?.capacity ?? this.capacity;


      this.billingCycle = selectedPlan.billing_cycle;
      this.selectedNumber = selectedPlan.cartInfo?.selected_number;
    }
  },
};
</script>

<style lang="scss">
.divide-line-transparent {
  width: 100%;
  border-top: 1px solid transparent;
  margin: 6px 0;
}
.divide-line {
  width: 100%;
  border-top: 1px solid var(--zoom-color-border-subtle-neutral, #DFE3E8);
  margin: 16px 0;
}
.opc-zs-trial {
  &__configDesc {
    padding-left: 60px;
  }
  &__additional-message {
    margin-top: 20px;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    color: var(--zoom-color-text-neutral, #686F79);
  }
  &__desc-text {
    display: flex;
    align-items: center;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    margin: 4 0;
    img {
      display: inline-block;
      margin-right: 4px;
    }
  }
}
</style>
